import { useState } from 'react'
import {
  Anchor, Button, Group, NumberInput, Select, Stack, TextInput, Textarea, Text,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus } from '@tabler/icons-react'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useQuery } from '@tanstack/react-query'
import {
  SERIES, formatearNumero, requerimientoEntrada,
  type Empresa, type Requerimiento, type RequerimientoEntrada,
} from '@serkat/shared'
import { api } from '../../../api/cliente'
import { aSelect, useCatalogos } from '../../../api/catalogos'
import { hoyLocal } from '../../../compartido/formato'
import { TablaItems } from './TablaItems'
import { ModalObra } from '../../obras/componentes/ModalObra'
import { ModalArea } from '../../areas/componentes/ModalArea'
import { useSesion } from '../../auth/useSesion'

interface Props {
  inicial?: Requerimiento
  guardando: boolean
  onGuardar: (datos: RequerimientoEntrada) => void
  onCancelar: () => void
}

export function FormularioRequerimiento({ inicial, guardando, onGuardar, onCancelar }: Props) {
  const { data: catalogos } = useCatalogos()

  // Mismo hueco que en el formulario de la orden, y antes en el recorrido:
  // quien levanta el requerimiento es el primero que topa con la obra o el
  // área que falta. Los modales son los del módulo de cada uno.
  const { usuario } = useSesion()
  const puedeCrearMaestros = usuario?.rol === 'PROCURA' || usuario?.rol === 'ADMIN'
  const [buscadoObra, setBuscadoObra] = useState('')
  const [obraNueva, modalObra] = useDisclosure(false)
  const [buscadaArea, setBuscadaArea] = useState('')
  const [areaNueva, modalArea] = useDisclosure(false)


  const { data: empresas } = useQuery({
    queryKey: ['empresas', 'activas'],
    queryFn: () => api.get<Empresa[]>('/empresas'),
  })

  // El correlativo propuesto NO se consume: el usuario puede cambiarlo.
  const { data: propuesto } = useQuery({
    queryKey: ['requerimientos', 'siguiente-numero'],
    queryFn: () => api.get<{ serie: string; correlativo: number }>('/requerimientos/siguiente-numero'),
    enabled: !inicial,
    staleTime: 0,
  })

  const form = useForm<RequerimientoEntrada>({
    initialValues: {
      empresaId: inicial?.empresaId ?? 0,
      correlativo: inicial?.correlativo ?? 0,
      fechaEmision: inicial?.fechaEmision ?? hoyLocal(),
      obraId: inicial?.obraId ?? null,
      areaId: inicial?.areaId ?? null,
      fechaMaximaAtencion: inicial?.fechaMaximaAtencion ?? null,
      observaciones: inicial?.observaciones ?? null,
      items: inicial?.items.map((i) => ({
        itemNumero: i.itemNumero,
        cantidad: i.cantidad,
        unidadId: i.unidadId,
        codPartida: i.codPartida,
        insumoId: i.insumoId,
        insumoServicio: i.insumoServicio,
        descripcionMarca: i.descripcionMarca,
        lugarDeUso: i.lugarDeUso,
      })) ?? [],
    },
    validate: zodResolver(requerimientoEntrada),
  })

  // Valores por defecto en cuanto llegan del servidor, sin pisar lo que se edita.
  if (!inicial) {
    if (propuesto && form.values.correlativo === 0) {
      form.setFieldValue('correlativo', propuesto.correlativo)
    }
  }

  const numero = form.values.correlativo
    ? formatearNumero(SERIES.REQUERIMIENTO, form.values.correlativo)
    : '—'

  return (
    <>
    <form onSubmit={form.onSubmit(onGuardar)}>
      <Stack gap="sm">
        <Group grow align="flex-start">
          <Select
            label="Empresa emisora"
            withAsterisk
            placeholder="Elija la empresa que emite"
            description="Se elige en cada documento; no hay una predeterminada" 
            data={(empresas ?? []).map((e) => ({ value: String(e.id), label: e.razonSocial }))}
            value={form.values.empresaId ? String(form.values.empresaId) : null}
            onChange={(v) => form.setFieldValue('empresaId', Number(v))}
            error={form.errors['empresaId']}
          />
          <NumberInput
            label="Correlativo"
            description={`Número: ${numero}`}
            withAsterisk
            min={1}
            hideControls
            {...form.getInputProps('correlativo')}
          />
        </Group>

        <Group grow align="flex-start">
          <TextInput label="Fecha de emisión" type="date" withAsterisk {...form.getInputProps('fechaEmision')} />
          <TextInput
            label="Fecha máxima de atención"
            type="date"
            value={form.values.fechaMaximaAtencion ?? ''}
            onChange={(e) => form.setFieldValue('fechaMaximaAtencion', e.currentTarget.value || null)}
            error={form.errors['fechaMaximaAtencion']}
          />
        </Group>

        <Group grow align="flex-start">
          <Stack gap={4}>
            <Select
              label="Obra"
              data={aSelect(catalogos?.obras)}
              clearable
              searchable
              nothingFoundMessage="Ninguna obra con ese nombre"
              onSearchChange={(v) => { if (v.trim()) setBuscadoObra(v) }}
              value={form.values.obraId ? String(form.values.obraId) : null}
              onChange={(v) => form.setFieldValue('obraId', v ? Number(v) : null)}
            />
            {puedeCrearMaestros && (
              <Anchor component="button" type="button" size="xs" onClick={modalObra.open}>
                <Group gap={4} wrap="nowrap">
                  <IconPlus size={12} />
                  Agregar obra
                </Group>
              </Anchor>
            )}
          </Stack>
          <Stack gap={4}>
            <Select
              label="Área solicitante"
              data={aSelect(catalogos?.areas)}
              clearable
              searchable
              nothingFoundMessage="Ninguna área con ese nombre"
              onSearchChange={(v) => { if (v.trim()) setBuscadaArea(v) }}
              value={form.values.areaId ? String(form.values.areaId) : null}
              onChange={(v) => form.setFieldValue('areaId', v ? Number(v) : null)}
            />
            {puedeCrearMaestros && (
              <Anchor component="button" type="button" size="xs" onClick={modalArea.open}>
                <Group gap={4} wrap="nowrap">
                  <IconPlus size={12} />
                  Agregar área
                </Group>
              </Anchor>
            )}
          </Stack>
        </Group>

        <TablaItems
          items={form.values.items}
          errores={form.errors as Record<string, string | undefined>}
          onCambiar={(items) => form.setFieldValue('items', items)}
        />

        <Textarea
          label="Observaciones"
          autosize
          minRows={2}
          value={form.values.observaciones ?? ''}
          onChange={(e) => form.setFieldValue('observaciones', e.currentTarget.value || null)}
        />

        <Group justify="space-between" mt="xs">
          <Text size="xs" c="dimmed">
            Se emite en estado Pendiente. Lo aprueban Procura, Dirección o Gerencia desde Validaciones.
          </Text>
          <Group>
            <Button variant="default" onClick={onCancelar}>Cancelar</Button>
            <Button type="submit" loading={guardando}>Guardar</Button>
          </Group>
        </Group>
      </Stack>
    </form>

    <ModalObra
      abierto={obraNueva}
      onCerrar={modalObra.close}
      nombreSugerido={form.values.obraId ? '' : buscadoObra}
      onGuardada={(o) => form.setFieldValue('obraId', o.id)}
    />

    <ModalArea
      abierto={areaNueva}
      onCerrar={modalArea.close}
      nombreSugerido={form.values.areaId ? '' : buscadaArea}
      onGuardada={(a) => form.setFieldValue('areaId', a.id)}
    />
</>
  )
}
